import { DatePicker, type DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import dayjs, { type Dayjs } from 'dayjs';

/**
 * DatePicker MUI já configurado para PT-BR (locale aplicado globalmente em
 * `providers.tsx` via `LocalizationProvider adapterLocale="pt-br"`).
 *
 * Aceita valor ISO `YYYY-MM-DD` (string) — converte de/para Dayjs internamente.
 * Para integração com React Hook Form, usar `DatePickerPtBrField`.
 */
export interface DatePickerPtBrProps extends Omit<
  DatePickerProps<Dayjs>,
  'value' | 'onChange' | 'format'
> {
  value: string | null;
  onChange: (isoDate: string | null) => void;
  format?: string;
}

export function DatePickerPtBr({
  value,
  onChange,
  format = 'DD/MM/YYYY',
  ...rest
}: DatePickerPtBrProps): JSX.Element {
  return (
    <DatePicker
      {...rest}
      format={format}
      value={value ? dayjs(value) : null}
      onChange={(d) => onChange(d && d.isValid() ? d.format('YYYY-MM-DD') : null)}
    />
  );
}

export interface DatePickerPtBrFieldProps<TFieldValues extends FieldValues> extends Omit<
  DatePickerPtBrProps,
  'value' | 'onChange'
> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
}

export function DatePickerPtBrField<TFieldValues extends FieldValues>({
  control,
  name,
  ...rest
}: DatePickerPtBrFieldProps<TFieldValues>): JSX.Element {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <DatePickerPtBr
          {...rest}
          value={(field.value as string | null | undefined) ?? null}
          onChange={(iso) => field.onChange(iso)}
          slotProps={{
            ...rest.slotProps,
            textField: {
              fullWidth: true,
              error: Boolean(fieldState.error),
              helperText: fieldState.error?.message,
              onBlur: field.onBlur,
              inputRef: field.ref,
              ...rest.slotProps?.textField,
            },
          }}
        />
      )}
    />
  );
}
